import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { bankIdApi } from '../services/api';
import type { AuthResponse, BankIdAuthSession, BankIdAuthStart } from '../types';

export type BankIdView = 'qr' | 'opening' | 'signing' | 'complete' | 'cancelled' | 'failed';
type BankIdMethod = 'qr' | 'same-device';
type BrowserSession = { id: string; token: string; completed: boolean; consuming: boolean };

// A landscape phone keeps the same-device default; tablets and desktop use QR.
const desktopLayoutQuery = '(min-width: 992px), (min-width: 768px) and (min-height: 576px)';
function useDesktopLayout() {
  const [desktop, setDesktop] = useState(() => window.matchMedia?.(desktopLayoutQuery).matches ?? true);
  useEffect(() => {
    const query = window.matchMedia?.(desktopLayoutQuery);
    if (!query) return;
    const update = () => setDesktop(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return desktop;
}

export function bankIdView(session: BankIdAuthSession, opening = false): BankIdView {
  if (session.status === 'cancelled' || session.hintCode === 'userCancel') return 'cancelled';
  if (['failed', 'expired'].includes(session.status) || ['startFailed', 'expiredTransaction', 'certificateErr'].includes(session.hintCode || '')) return 'failed';
  if (session.status === 'complete') return 'complete';
  if (['userSign', 'userSigning', 'SIGNING'].includes(session.hintCode || '')) return 'signing';
  return opening ? 'opening' : 'qr';
}

function errorKey(error: unknown): string {
  const code = (error as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
  if (code === 'BANKID_ACCOUNT_NOT_FOUND') return 'auth.bankid.noAccount';
  if (code === 'BANKID_ACCOUNT_ROLE_MISMATCH') return 'auth.bankid.accountRoleMismatch';
  if (code === 'BANKID_IDENTITY_CONFLICT' || code === 'BANKID_IDENTITY_REVIEW_REQUIRED') return 'auth.bankid.identityConflict';
  if (code === 'BANKID_NOT_ENABLED' || code === 'BANKID_UNAVAILABLE') return 'auth.bankid.unavailable';
  return 'auth.bankid.failureHelp';
}

function launchUrl(value: string | null | undefined, mobile: boolean): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol === 'bankid:' && !url.host && url.pathname === '/') {
      if (!mobile) return url.href;
      const universalLink = new URL('https://app.bankid.com/');
      universalLink.search = url.search;
      return universalLink.href;
    }
    if (url.protocol === 'https:' && ['app.bankid.com', 'bankid.com'].includes(url.hostname) && !url.port && !url.username && !url.password) return url.href;
  } catch { /* Only official launch URLs are offered. */ }
  return null;
}

export function BankIdFlow({ request, onContinue, onBusyChange }: {
  request: BankIdAuthStart;
  onContinue: (response: AuthResponse) => Promise<void>;
  onBusyChange?: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState<BankIdView>('qr');
  const [session, setSession] = useState<BankIdAuthSession | null>(null);
  const [result, setResult] = useState<AuthResponse | null>(null);
  const [qr, setQr] = useState('');
  const [failure, setFailure] = useState('auth.bankid.failureHelp');
  const [continuing, setContinuing] = useState(false);
  const [consuming, setConsuming] = useState(false);
  const desktopLayout = useDesktopLayout();
  const [chosenMethod, setChosenMethod] = useState<BankIdMethod | null>(null);
  const method = chosenMethod ?? (desktopLayout ? 'qr' : 'same-device');
  const active = useRef<BrowserSession | null>(null);
  const opening = useRef(false);
  const cancelled = useRef(false);
  const { purpose, personaType, invitationId } = request;

  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let browserSession: BrowserSession | null = null;
    cancelled.current = false;
    opening.current = false;
    setSession(null); setResult(null); setView('qr'); setQr(''); setConsuming(false); setContinuing(false); setFailure('auth.bankid.failureHelp');
    const update = async (next: BankIdAuthSession) => {
      if (disposed || cancelled.current) return;
      setSession(next);
      const nextView = bankIdView(next, opening.current);
      if (nextView === 'complete' && browserSession) {
        // Identification is a preview: commit the selected registration role on Next.
        if (purpose === 'register') { setView('complete'); return; }
        // Consume exactly once; polling never creates a second user session.
        setConsuming(true);
        setView('signing');
        const response = await bankIdApi.complete(browserSession.id, browserSession.token);
        browserSession.completed = true;
        if (!disposed && !cancelled.current) { setResult(response); setView('complete'); }
        return;
      }
      setView(nextView);
      if (nextView === 'cancelled' || nextView === 'failed') return;
      if (next.sessionExpiresAt && Date.parse(next.sessionExpiresAt) <= Date.now()) {
        setView('failed'); return;
      }
      timer = setTimeout(async () => {
        if (!browserSession || disposed || cancelled.current) return;
        try { await update(await bankIdApi.getStatus(browserSession.id, browserSession.token)); }
        catch (error) { if (!disposed && !cancelled.current) { setFailure(errorKey(error)); setView('failed'); } }
      }, Math.max(1000, next.pollIntervalMs || 1000));
    };
    void bankIdApi.start({ purpose, personaType, invitationId }).then(async next => {
      if (!next.sessionToken) throw new Error('Missing BankID browser session');
      browserSession = { id: next.id, token: next.sessionToken, completed: false, consuming: false };
      if (disposed || cancelled.current) {
        await bankIdApi.cancel(browserSession.id, browserSession.token).catch(() => {});
        return;
      }
      active.current = browserSession;
      await update(next);
    }).catch(error => { if (!disposed && !cancelled.current) { setFailure(errorKey(error)); setView('failed'); } });
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      if (browserSession && !browserSession.completed) void bankIdApi.cancel(browserSession.id, browserSession.token).catch(() => {});
      active.current = null;
    };
  }, [attempt, purpose, personaType, invitationId]);

  useEffect(() => {
    let disposed = false;
    const data = session?.qrCode?.data;
    if (!data) { setQr(''); return; }
    void QRCode.toDataURL(data, { width: 200, margin: 2, errorCorrectionLevel: 'M' })
      .then(image => { if (!disposed && !cancelled.current) setQr(image); })
      .catch(() => { if (!disposed && !cancelled.current) { setFailure('auth.bankid.failureHelp'); setView('failed'); } });
    return () => { disposed = true; };
  }, [session?.qrCode?.data]);

  const cancel = async () => {
    cancelled.current = true;
    setView('cancelled'); setQr('');
    const current = active.current;
    if (current) await bankIdApi.cancel(current.id, current.token).catch(() => {});
  };
  // Platform selects BankID's supported launch mechanism; resizing only changes priority.
  const mobilePlatform = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const sameDeviceUrl = launchUrl(session?.sameDeviceUrl, mobilePlatform);
  const chooseMethod = (next: BankIdMethod) => {
    setChosenMethod(next);
    opening.current = false;
    if (session) setView(bankIdView(session));
  };
  const openApp = () => {
    setChosenMethod('same-device');
    opening.current = true;
    setView('opening');
  };
  const choosingMethod = view === 'qr' || view === 'opening';
  const launchClass = method === 'qr' ? 'btn-outline-primary bankid-device' : 'btn-primary bankid-action';
  const launchLabel = method === 'qr' ? 'auth.bankid.useThisDevice' : 'auth.bankid.open';
  return <section className="bankid-flow text-center" aria-live="polite" aria-busy={!session && view === 'qr'}>
    {!['complete', 'cancelled'].includes(view) && <div className="bankid-logo-clearspace"><img src="/assets/images/BankID_logo.svg" alt={t('auth.bankid.logoAlt')} width="44" className="img-fluid" /></div>}
    {view === 'qr' && method === 'qr' && <>
      <div className="bankid-qr mx-auto mb-3 d-flex align-items-center justify-content-center">
        {qr ? <img src={qr} width="200" height="200" alt={t('auth.bankid.qrAlt')} className="img-fluid" /> : <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>}
      </div>
      <p>{t('auth.bankid.scan')}</p>
    </>}
    {view === 'qr' && method === 'same-device' && <p>{t('auth.bankid.sameDeviceHelp')}</p>}
    {(view === 'opening' || view === 'signing') && <><div className="spinner-border text-primary my-4" aria-hidden="true" /><p>{t(`auth.bankid.${view}`)}</p></>}
    {choosingMethod && <>
      {sameDeviceUrl ? <a href={sameDeviceUrl} referrerPolicy="origin" className={`btn ${launchClass} w-100 justify-content-center`} onClick={openApp}>{t(launchLabel)}</a>
        : <button type="button" className={`btn ${launchClass} w-100 justify-content-center`} disabled>{!session ? t('common.loading') : t(launchLabel)}</button>}
      {session && !sameDeviceUrl && <p className="text-muted small mt-3" role="status">{t('auth.bankid.sameDeviceUnavailable')}</p>}
      {method === 'same-device' && session?.hintCode === 'noClient' && <p className="text-muted small mt-3" role="status">{t('auth.bankid.noClient')}</p>}
    </>}
    {choosingMethod && method === 'same-device' && <button type="button" className="btn btn-link mx-auto mt-3" onClick={() => chooseMethod('qr')}>
      {t('auth.bankid.useQr')}
    </button>}
    {['qr', 'opening', 'signing'].includes(view) && <>
      <button type="button" className="btn btn-link mx-auto mt-2" disabled={consuming} onClick={() => void cancel()}>{t('common.cancel')}</button>
    </>}
    {view === 'complete' && session && (purpose === 'register' || result) && <>
      <div className="bankid-success-circle" aria-hidden="true"><i className="bi bi-check-lg" /></div>
      <h2 className="h4">{t(purpose === 'register' ? 'auth.bankid.identified' : 'auth.bankid.loggedIn')}</h2>
      {purpose === 'register' && <p className="fw-semibold">{session.identity?.name}</p>}
      <button className="btn btn-primary bankid-action w-100 justify-content-center" disabled={continuing} onClick={async () => {
        const current = active.current;
        if (!current || current.consuming) return;
        current.consuming = true;
        setContinuing(true);
        onBusyChange?.(true);
        try {
          const response = result ?? await bankIdApi.complete(current.id, current.token);
          current.completed = true;
          if (active.current !== current || cancelled.current) return;
          setResult(response);
          await onContinue(response);
        } catch (error) {
          if (active.current === current && !cancelled.current) { setFailure(errorKey(error)); setView('failed'); }
        } finally {
          current.consuming = false;
          if (active.current === current) setContinuing(false);
          onBusyChange?.(false);
        }
      }}>{t(purpose === 'register' ? 'common.next' : 'auth.bankid.continue')}</button>
    </>}
    {(view === 'cancelled' || view === 'failed') && <>
      <div role={view === 'failed' ? 'alert' : 'status'}>
        <h2 className="h5">{t(view === 'cancelled' ? 'auth.bankid.cancelled' : 'auth.bankid.failed')}</h2>
        <p className="text-muted">{t(view === 'cancelled' ? purpose === 'register' ? 'auth.bankid.noAccountCreated' : 'auth.bankid.cancelledHelp' : failure)}</p>
      </div>
      <button type="button" className="btn btn-primary bankid-action w-100 justify-content-center" onClick={() => setAttempt(a => a + 1)}>{t('auth.bankid.retry')}</button>
    </>}
  </section>;
}
